/**
 * Catering Packages — action-type constants + state shape.
 * Lives at `state.catering.packages` (registered in `cateringReducer.ts`; saga forked from `cateringSaga.ts`).
 * Tags are NOT part of this slice — they are a catering setting (`catering_package_tags`, `state.catering.settings`).
 */
import type {
  CateringPackage,
  PackageCatalogItem,
  PackageItemRef,
} from '../../components/catering/data/types';

export const CPK = {
  FETCH_REQUEST: 'cateringPackages/FETCH_REQUEST',
  FETCH_SUCCESS: 'cateringPackages/FETCH_SUCCESS',
  FETCH_FAILURE: 'cateringPackages/FETCH_FAILURE',

  PACKAGE_CREATE_REQUEST: 'cateringPackages/PACKAGE_CREATE_REQUEST',
  PACKAGE_CREATE_SUCCESS: 'cateringPackages/PACKAGE_CREATE_SUCCESS',
  PACKAGE_CREATE_FAILURE: 'cateringPackages/PACKAGE_CREATE_FAILURE',

  PACKAGE_UPDATE_REQUEST: 'cateringPackages/PACKAGE_UPDATE_REQUEST',
  PACKAGE_UPDATE_SUCCESS: 'cateringPackages/PACKAGE_UPDATE_SUCCESS',
  PACKAGE_UPDATE_FAILURE: 'cateringPackages/PACKAGE_UPDATE_FAILURE',

  PACKAGE_DUPLICATE_REQUEST: 'cateringPackages/PACKAGE_DUPLICATE_REQUEST',
  PACKAGE_DUPLICATE_SUCCESS: 'cateringPackages/PACKAGE_DUPLICATE_SUCCESS',
  PACKAGE_DUPLICATE_FAILURE: 'cateringPackages/PACKAGE_DUPLICATE_FAILURE',

  /** Optimistic: the reducer removes the package on REQUEST; a failure refetches to reconcile. */
  PACKAGE_DELETE_REQUEST: 'cateringPackages/PACKAGE_DELETE_REQUEST',
  PACKAGE_DELETE_SUCCESS: 'cateringPackages/PACKAGE_DELETE_SUCCESS',
  PACKAGE_DELETE_FAILURE: 'cateringPackages/PACKAGE_DELETE_FAILURE',

  /** Optimistic drag-reorder: the reducer applies the new order on REQUEST; a failure refetches. */
  REORDER_REQUEST: 'cateringPackages/REORDER_REQUEST',
  REORDER_SUCCESS: 'cateringPackages/REORDER_SUCCESS',
  REORDER_FAILURE: 'cateringPackages/REORDER_FAILURE',

  /** One package for the details / edit screen, from the dedicated `GET /catering/packages/:id` (not picked from the list). */
  DETAIL_FETCH_REQUEST: 'cateringPackages/DETAIL_FETCH_REQUEST',
  DETAIL_FETCH_SUCCESS: 'cateringPackages/DETAIL_FETCH_SUCCESS',
  DETAIL_FETCH_FAILURE: 'cateringPackages/DETAIL_FETCH_FAILURE',

  /** Package item picker data: catering-enabled catalogue items with their tray sizes. */
  CATALOG_FETCH_REQUEST: 'cateringPackages/CATALOG_FETCH_REQUEST',
  CATALOG_FETCH_SUCCESS: 'cateringPackages/CATALOG_FETCH_SUCCESS',
  CATALOG_FETCH_FAILURE: 'cateringPackages/CATALOG_FETCH_FAILURE',

  CLEAR_SAVE_STATE: 'cateringPackages/CLEAR_SAVE_STATE',
} as const;

export interface CateringPackagesState {
  /** All packages in list order (`sortOrder`, created, name); each carries its `tags` and `variants`. */
  packages: CateringPackage[];
  loading: boolean;
  /** true after the first successful fetch for `loadedLocationId`. */
  loaded: boolean;
  loadedLocationId: string | null;
  error: string | null;
  /** A create / update / duplicate / delete call is in flight. */
  saving: boolean;
  savingError: string | null;
  /** Server error `code` of the last failed save (e.g. `INVALID_PACKAGE_ITEM`, `INVALID_PACKAGE_VARIANT`, `INVALID_PACKAGE_TAG`). */
  savingErrorCode: string | null;
  /** HTTP status of the last failed save. */
  savingErrorStatus: number | null;
  /** Structured server context of the last failed save (e.g. `{ indexes: number[] }`, `{ tagIds: string[] }`). */
  savingErrorDetails: Record<string, unknown> | null;
  /** Set when a drag-reorder failed to persist (the list is refetched to reconcile). Separate from `saving*` so a drag never triggers save toasts. */
  reorderError: string | null;
  /** Id of the package saved by the last successful create/update/duplicate. */
  lastSavedPackageId: string | null;
  /**
   * Set when the PACKAGE saved but its image step (upload / remove) failed. Not a save failure: the package
   * exists and `savingError` stays null. The form uses it to keep the user on the right screen with a toast.
   */
  imageError: string | null;
  /**
   * The package behind the details / edit screen, loaded with `GET /catering/packages/:id`. The list is for list
   * views only. Cleared on every request (a details screen always starts from fresh data, never a stale copy) and
   * kept current by this screen's own save (the POST / PATCH response is the same package object).
   */
  detail: CateringPackage | null;
  /** The id the details screen asked for — a package is only ever shown for the id that was requested. */
  detailId: string | null;
  detailLoading: boolean;
  detailError: string | null;
  /** The BE said 404 `NOT_FOUND`: no such package, it was deleted, or it belongs to another location. */
  detailNotFound: boolean;
  /** Catering-enabled catalogue items for the package item picker (flat list, tray sizes included). */
  catalogItems: PackageCatalogItem[];
  catalogLoading: boolean;
  /** true after the first successful catalogue fetch for `catalogLocationId`. */
  catalogLoaded: boolean;
  catalogLocationId: string | null;
  catalogError: string | null;
}

/**
 * One variant as the form edits it. `id` is present for a saved variant (update) and absent for a new one.
 * Exactly one draft must be `isDefault`. `packageItems` omitted = items untouched; `[]` = cleared.
 */
export interface PackageVariantDraft {
  id?: string;
  name?: string | null;
  serves: string;
  price: number;
  isDefault: boolean;
  packageItems?: PackageItemRef[];
  /** This serving option's notes; blank = none. */
  notes?: string;
}

/** What to do with the package image on save. The saga runs it AFTER the package itself has saved. */
export interface PackageImageChange {
  /** New image to upload to the package (single image: it replaces the current one). */
  file?: File;
  /** Saved media ids to delete (the current image when it is removed or replaced). */
  removeMediaIds?: string[];
}

/** Fields the package form edits. On update, omitted = unchanged (PATCH semantics). */
export interface PackageSavePayload {
  name?: string;
  notes?: string | null;
  minQtyRequired?: number | null;
  maxQtyRequired?: number | null;
  sortOrder?: number;
  /** Whole-set replace when present; `[]` removes every tag. Ids come from the settings tag list. */
  tagIds?: string[];
  /** Whole-set replace when present: a saved variant missing from the list is removed. Never empty. */
  variants?: PackageVariantDraft[];
  /** `YYYY-MM-DD`. Update: omitted = unchanged, `null` = clear. Create: only set values are sent (`null`/omitted = no limit). */
  orderStartingDate?: string | null;
  packageAvailabilityFrom?: string | null;
  packageAvailabilityTo?: string | null;
  /** Not part of the package body — handled by the saga after the package saves. */
  image?: PackageImageChange;
}

export interface CreatePackagePayload extends PackageSavePayload {
  name: string;
  /** At least one (the default). A package with no extra serving options is just one variant. */
  variants: PackageVariantDraft[];
}

export interface PackageFailure {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}
